import { FC, useState, useCallback } from 'react';
import { enqueueSnackbar } from 'notistack';
import { Box } from '@mui/material';
import { useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { CustomOutputFileTypeEnum, CustomOutputFileCategoryEnum } from 'entities/Extensions/enums';
import { patchCustomOutputFile } from 'entities/Extensions/api';
import { CustomOutputFileType } from 'entities/Extensions/types';
import { TabButton } from 'shared/ui/TabButton';
import { Loader } from 'shared/ui/Loader';
import { AxiosError } from 'axios';
import { showErrorNotification } from 'shared/helpers/showErrorNotification';
import { CustomOutputFileTable } from 'widgets/Extensions/Details/ui/CustomOutputFile/ui/OutputTypeView/Table';
import { useCustomOutputFileStore } from 'widgets/Extensions/Details/ui/CustomOutputFile/model/state';

const customOutputFileTabs = [
	{ type: CustomOutputFileTypeEnum.prebuild, name: 'extensions.ex_list.output_file.standard_tab' },
	{ type: CustomOutputFileTypeEnum.custom, name: 'extensions.ex_list.output_file.custom_tab' },
	{ type: CustomOutputFileTypeEnum.generative, name: 'extensions.ex_list.output_file.generative_tab' },
];

type OutputTypeViewPropsType = {
	loading: boolean;
	outputTypeData: CustomOutputFileType[];
	outputType: CustomOutputFileTypeEnum;
};

export const OutputTypeView: FC<OutputTypeViewPropsType> = ({ loading, outputTypeData, outputType }) => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();
	const { setTabs, setSelectedOutputType } = useCustomOutputFileStore();
	const [, setSearchParams] = useSearchParams();
	const [activeTabType, setActiveTabType] = useState(outputType);

	const { mutate: customOutputMutate } = useMutation({
		mutationKey: ['patch/custom-output-file'],
		mutationFn: patchCustomOutputFile,
	});

	const toggleSwitchHandler = useCallback((data: { id: string; isEnabled: boolean }) => {
		customOutputMutate(
			{
				id: data.id,
				data: { isEnabled: data.isEnabled },
			},
			{
				onSuccess: async () => {
					await queryClient.refetchQueries({ queryKey: ['get/custom-output-file', outputType] });
					enqueueSnackbar({
						message: t('shared_texts.success_update'),
						variant: 'success',
					});
				},
				onError: (err) => {
					showErrorNotification(err as AxiosError);
				},
			},
		);
	}, []);

	const customOutputFileDetailsHandler = useCallback(
		(typeId: string) => {
			const outputTypeObj = outputTypeData.find((item) => item._id === typeId);
			setSelectedOutputType(outputTypeObj);
			if (
				outputTypeObj?.category !== CustomOutputFileCategoryEnum.custom &&
				outputTypeObj?.category !== CustomOutputFileCategoryEnum.generative
			) {
				const newTabs = outputTypeData
					.filter((item) => !item.isMergeExport)
					.map((item) => ({
						type: item._id,
						name: t(`sidebar.menu./files/${item?.category}`),
					}));
				setTabs(newTabs);
			} else {
				const newTabs = outputTypeData
					.filter((item) => !item.isMergeExport)
					.map((item) => ({
						type: item._id,
						name: item?.generativeModelId?.name || item?.customModelId?.name,
					}));
				setTabs(newTabs);
			}
		},
		[outputTypeData],
	);

	return (
		<>
			{loading ? (
				<Loader />
			) : (
				<>
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'row',
							justifyContent: 'flex-start',
							alignItems: 'center',
							gap: '20px',
							marginBottom: '20px',
						}}
					>
						{customOutputFileTabs.map(({ type, name }) => (
							<TabButton
								key={type}
								label={t(name)}
								isActive={type === activeTabType}
								action={() => {
									setActiveTabType(type);
									setSearchParams({ type });
								}}
							/>
						))}
					</Box>

					<CustomOutputFileTable
						data={outputTypeData}
						toggleSwitchHandler={toggleSwitchHandler}
						customOutputFileDetailsHandler={customOutputFileDetailsHandler}
					/>
				</>
			)}
		</>
	);
};
